import { log, logE, logV } from "../../../shared/services/debug-logger";
import type {
  CronJobLogEnvironment,
  ReingestTechFormInboxRequest,
  TechFormInboxBodyPreview,
  TechFormInboxEmailStatus,
  TechFormInboxPreviewResponse,
} from "../../models/admin-monitoring.models";
import {
  getAdminTechFormInboxDebugErrorMessage,
  getTechFormInboxDebugBody,
  getTechFormInboxDebugPreview,
  reingestTechFormInboxDebugAttachments,
} from "../../services/admin-tech-form-inbox-debug.service";
import { adminStyles } from "../../styles/admin.styles";
import { getTechFormInboxAttachmentDiagnosticSummary } from "../../utils/admin-debug.utils";
import { ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const TECH_FORM_RETURN_MAILBOX = "Ot-geneva_Auto-form-sending@cargill.com";

const statusClasses: Record<TechFormInboxEmailStatus, string> = {
  saved: "bg-[#CCE4E6] text-[#007681]",
  not_saved: "bg-[#FFF8E1] text-[#946200]",
  broken: "bg-[#FFEAED] text-[#BF0002]",
  not_otf: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  unknown: "bg-[#FFF8E1] text-[#946200]",
  no_json: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300",
};

function formatOtfStatus(status: TechFormInboxEmailStatus | undefined): string {
  return (status ?? "unknown").replaceAll("_", " ").toUpperCase();
}

function formatInboxDate(value: string): string {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value || "N/A" : date.toLocaleString();
}

interface Props {
  environment: CronJobLogEnvironment;
}

// Lets the development team compare mailbox messages with the exact Snowflake state used by OTF import.
// Stays in the Debug section because it reads shared mail and can start controlled re-ingestion writes.
const TechFormInboxDebugPanel = ({ environment }: Props) => {
  const [mailbox, setMailbox] = useState(TECH_FORM_RETURN_MAILBOX);
  const [imoNumber, setImoNumber] = useState("");
  const [data, setData] = useState<TechFormInboxPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [reingesting, setReingesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([undefined]);
  const [bodies, setBodies] = useState<Record<string, TechFormInboxBodyPreview>>({});
  const [bodyErrors, setBodyErrors] = useState<Record<string, string>>({});
  const [loadingBodyId, setLoadingBodyId] = useState<string | null>(null);
  const scanRequestId = useRef(0);

  useEffect(() => {
    scanRequestId.current += 1;
    setData(null);
    setErrorMessage(null);
    setLoading(false);
    setReingesting(false);
    setActionMessage(null);
    setActionError(null);
    setPageIndex(0);
    setPageCursors([undefined]);
    setBodies({});
    setBodyErrors({});
    setLoadingBodyId(null);

    return () => {
      scanRequestId.current += 1;
    };
  }, [environment]);

  const statusMetrics = useMemo(
    () =>
      data
        ? [
            { label: "Emails", value: data.health.messagesScanned, status: "no_json" as const },
            { label: "Saved emails", value: data.health.savedEmails, status: "saved" as const },
            {
              label: "Need re-ingestion",
              value: data.health.reingestionRequiredEmails,
              status: "broken" as const,
            },
            { label: "Not saved", value: data.health.notSavedAttachments, status: "not_saved" as const },
            { label: "Broken", value: data.health.brokenAttachments, status: "broken" as const },
            { label: "Non-OTF emails", value: data.health.nonOtfEmails, status: "not_otf" as const },
            { label: "Unknown", value: data.health.unknownAttachments, status: "unknown" as const },
          ]
        : [],
    [data],
  );

  const scanInbox = async (cursor?: string, targetPage = 0) => {
    const normalizedMailbox = mailbox.trim();

    if (!normalizedMailbox) {
      log("TechFormInboxDebugPanel - scanInbox - missing input");
      setErrorMessage("Mailbox is required.");

      return;
    }

    log("TechFormInboxDebugPanel - scanInbox", { environment, page: targetPage });
    setLoading(true);
    setData(null);
    setBodies({});
    setBodyErrors({});
    setLoadingBodyId(null);
    setErrorMessage(null);
    setActionError(null);
    const requestId = ++scanRequestId.current;

    try {
      const response = await getTechFormInboxDebugPreview(normalizedMailbox, environment, imoNumber, cursor);

      if (scanRequestId.current === requestId) {
        log("TechFormInboxDebugPanel - scanInbox - applied", {
          count: response.health.messagesScanned,
          correlated: response.health.snowflakeCorrelationOk,
        });
        setData(response);
        setPageIndex(targetPage);
      } else {
        log("TechFormInboxDebugPanel - scanInbox - stale");
      }
    } catch (error) {
      if (scanRequestId.current === requestId) {
        logE(error, { operation: "TechFormInboxDebugPanel - scanInbox - failed" });
        setErrorMessage(getAdminTechFormInboxDebugErrorMessage(error));
      }
    } finally {
      if (scanRequestId.current === requestId) {
        setLoading(false);
      }
    }
  };

  const inspectBody = async (messageId: string) => {
    if (loadingBodyId || !data) return;
    log("TechFormInboxDebugPanel - inspectBody");
    const requestId = scanRequestId.current;
    setLoadingBodyId(messageId);
    setBodyErrors((current) => ({ ...current, [messageId]: "" }));
    try {
      const body = await getTechFormInboxDebugBody(data.mailbox, messageId);
      if (scanRequestId.current === requestId) {
        log("TechFormInboxDebugPanel - inspectBody - applied");
        setBodies((current) => ({ ...current, [messageId]: body }));
      }
    } catch (error) {
      if (scanRequestId.current === requestId) {
        logE(error, { operation: "TechFormInboxDebugPanel - inspectBody - failed" });
        setBodyErrors((current) => ({
          ...current,
          [messageId]: getAdminTechFormInboxDebugErrorMessage(error),
        }));
      }
    } finally {
      if (scanRequestId.current === requestId) setLoadingBodyId(null);
    }
  };

  const startFreshScan = () => {
    setPageCursors([undefined]);
    setPageIndex(0);
    setActionMessage(null);
    void scanInbox(undefined, 0);
  };

  const reingestTargets = async (targets: ReingestTechFormInboxRequest["targets"]) => {
    if (!data || targets.length === 0 || reingesting) {
      log("TechFormInboxDebugPanel - reingestTargets - skipped", { count: targets.length, pending: reingesting });
      return;
    }

    const confirmed = window.confirm(
      `Re-ingest ${targets.length} offline Tech Form email attachment${targets.length === 1 ? "" : "s"} into ${environment}? The backend will re-check every target before saving.`,
    );
    if (!confirmed) {
      log("TechFormInboxDebugPanel - reingestTargets - cancelled");
      return;
    }

    log("TechFormInboxDebugPanel - reingestTargets", { count: targets.length, environment });
    setReingesting(true);
    setActionMessage(null);
    setActionError(null);
    try {
      const result = await reingestTechFormInboxDebugAttachments({ mailbox: data.mailbox, environment, targets });
      log("TechFormInboxDebugPanel - reingestTargets - completed", {
        processed: result.processedCount,
        skipped: result.skippedCount,
        failed: result.failedCount,
      });
      setActionMessage(result.summary);
      await scanInbox(pageCursors[pageIndex], pageIndex);
    } catch (error) {
      logE(error, { operation: "TechFormInboxDebugPanel - reingestTargets - failed" });
      setActionError(getAdminTechFormInboxDebugErrorMessage(error));
    } finally {
      setReingesting(false);
    }
  };

  const recoveryTargets = useMemo(
    () =>
      data?.results
        .filter((result) => result.reingestionRequired)
        .map((result) => ({ messageId: result.messageId, attachmentId: result.attachmentId })) ?? [],
    [data],
  );

  const chartMaximum = useMemo(
    () => Math.max(1, ...(data?.dailyCounts.map((count) => count.receivedEmails) ?? [1])),
    [data],
  );

  logV("TechFormInboxDebugPanel - BUILD", { loading, reingesting, page: pageIndex });

  return (
    <section className={twMerge(adminStyles.card.surface, "p-5")} data-test="admin-debug-tech-form-inbox-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase text-green">Offline inbox</p>
          <h2 className="mt-1 text-lg font-semibold">Mailbox and Snowflake correlation</h2>
          <p className="mt-2 max-w-3xl text-sm opacity-75">
            Scans 100 messages per page, lists all mail, and verifies returned OTF JSON against the canonical{" "}
            {environment}
            submission and TQ rows. Scanning is read-only; re-ingestion is always a separate confirmed action.
          </p>
        </div>
        <span className="bg-[#E9F4F3] px-3 py-1 text-xs font-semibold text-green">{environment}</span>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(240px,1fr)_180px_auto] lg:items-end">
        <label className="text-sm">
          <span className="mb-1 block font-medium">Mailbox</span>
          <input
            type="email"
            value={mailbox}
            data-test="admin-debug-tech-form-inbox-mailbox"
            className="h-10 w-full border border-gray-300 bg-transparent px-3 outline-none focus:border-green dark:border-gray-700"
            onChange={(event) => setMailbox(event.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium">IMO filter</span>
          <input
            type="text"
            value={imoNumber}
            placeholder="Optional"
            data-test="admin-debug-tech-form-inbox-imo"
            className="h-10 w-full border border-gray-300 bg-transparent px-3 outline-none focus:border-green dark:border-gray-700"
            onChange={(event) => setImoNumber(event.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={loading || !mailbox.trim()}
          data-test="admin-debug-tech-form-inbox-scan"
          className="inline-flex h-10 items-center justify-center gap-2 bg-green px-4 text-sm font-semibold text-white transition-colors hover:bg-[#166b60] disabled:opacity-50"
          onClick={startFreshScan}
        >
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <MagnifyingGlassIcon className="h-5 w-5" />}
          {loading ? "Scanning" : "Scan inbox"}
        </button>
      </div>

      {errorMessage && (
        <div
          className="mt-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]"
          data-test="admin-debug-tech-form-inbox-error"
        >
          {errorMessage}
        </div>
      )}

      {actionError && (
        <div className="mt-4 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]">{actionError}</div>
      )}

      {actionMessage && (
        <div className="mt-4 border border-[#7BC4B8] bg-[#E9F4F3] px-4 py-3 text-sm text-green">{actionMessage}</div>
      )}

      {data && (
        <div className="mt-5 space-y-5" data-test="admin-debug-tech-form-inbox-results">
          <p className="border-l-2 border-green bg-[#E9F4F3] px-4 py-3 text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-100">
            {data.summary}
          </p>

          <details
            className="border-greyscale-20 border bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
            data-test="admin-monitoring-tech-form-scan-json"
          >
            <summary
              className="cursor-pointer text-xs font-semibold"
              data-test="admin-monitoring-tech-form-scan-json-toggle"
            >
              Inspect scan response JSON
            </summary>
            <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs leading-5">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            {statusMetrics.map((metric) => (
              <div key={metric.label} className={twMerge("p-3", statusClasses[metric.status])}>
                <p className="text-[10px] font-semibold uppercase opacity-75">{metric.label}</p>
                <p className="mt-2 text-3xl font-bold">{metric.value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border border-[#F59692] bg-[#FFF7F8] p-4 dark:bg-gray-800">
            <div>
              <p className="text-sm font-semibold text-[#BF0002]" data-test="admin-monitoring-tech-form-repair-count">
                {data.health.reingestionRequiredEmails} email(s) need re-ingestion on this page
              </p>
              <p className="mt-1 text-xs opacity-75">
                These are valid OTF returns whose canonical TQ rows are missing, even when their submission flags say
                OK.
              </p>
              {!data.health.reingestionAllowed && (
                <p className="mt-1 text-xs font-semibold text-[#946200]">
                  Recovery is disabled here: this backend writes {data.health.runtimeEnvironment}, while you are viewing{" "}
                  {environment}.
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={reingesting || recoveryTargets.length === 0 || !data.health.reingestionAllowed}
              data-test="admin-monitoring-tech-form-reingest-missing"
              className="inline-flex h-10 items-center gap-2 bg-[#BF0002] px-4 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => void reingestTargets(recoveryTargets)}
            >
              <ArrowPathIcon className={twMerge("h-5 w-5", reingesting && "animate-spin")} />
              {reingesting ? "Re-ingesting" : `Re-ingest missing (${recoveryTargets.length})`}
            </button>
          </div>

          <div
            className="border-greyscale-20 border p-4 dark:border-gray-700"
            data-test="admin-monitoring-tech-form-volume-chart"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold">Received emails vs verified OTF saves</h3>
              <p className="text-xs opacity-60">Current 100-email page</p>
            </div>
            <div className="mt-4 space-y-3">
              {data.dailyCounts.map((count) => (
                <div key={count.date} className="grid gap-2 text-xs md:grid-cols-[90px_1fr_1fr] md:items-center">
                  <span className="font-medium">{count.date}</span>
                  <div>
                    <div className="mb-1 flex justify-between gap-2">
                      <span>Received</span>
                      <span>{count.receivedEmails}</span>
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-700">
                      <div
                        className="h-3 bg-[#64748B]"
                        style={{ width: `${(count.receivedEmails / chartMaximum) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between gap-2">
                      <span>OTF saved</span>
                      <span>
                        {count.savedOtfEmails}
                        {count.reingestionRequiredEmails ? ` | ${count.reingestionRequiredEmails} failed` : ""}
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-gray-700">
                      <div
                        className="h-3 bg-green"
                        style={{ width: `${(count.savedOtfEmails / chartMaximum) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {data.dailyCounts.length === 0 && (
                <p className="text-sm opacity-70">No email volume to graph on this page.</p>
              )}
            </div>
          </div>

          {!data.health.snowflakeCorrelationOk && (
            <p className="border border-[#F6CC72] bg-[#FFF8E1] px-4 py-3 text-sm text-[#946200]">
              Snowflake correlation could not be completed: {data.health.snowflakeCorrelationError ?? "Unknown error"}
            </p>
          )}

          <div>
            <h3 className="mb-3 text-sm font-semibold">Received emails</h3>
            <p className="mb-3 text-xs opacity-70">
              Short body previews are checked for Tech Form JSON clues. Open an email body to inspect and scan its full
              text.
            </p>
            <div className="overflow-x-auto">
              <table
                className="min-w-full border-collapse text-left text-xs"
                data-test="admin-monitoring-tech-form-email-table"
              >
                <thead>
                  <tr className="border-greyscale-20 border-b dark:border-gray-700">
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">OTF status</th>
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">Received</th>
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">From</th>
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">Subject</th>
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">Summary</th>
                    <th className="px-3 py-2 font-semibold uppercase opacity-70">Body</th>
                  </tr>
                </thead>
                <tbody>
                  {data.messages.length > 0 ? (
                    data.messages.map((message) => {
                      const status = message.otfStatus ?? "unknown";
                      const bodyResult = bodies[message.id];
                      const bodyWarning = bodyResult?.bodyJsonWarning ?? message.bodyJsonWarning;
                      const bodyError = bodyErrors[message.id];

                      return (
                        <tr
                          key={message.id}
                          className="border-greyscale-20 border-b last:border-0 dark:border-gray-700"
                        >
                          <td className="whitespace-nowrap px-3 py-3 align-top">
                            <span
                              className={twMerge(
                                "inline-flex px-2 py-1 text-[10px] font-semibold",
                                statusClasses[status],
                              )}
                            >
                              {formatOtfStatus(status)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 align-top">
                            {formatInboxDate(message.receivedDateTime)}
                          </td>
                          <td className="max-w-[220px] break-words px-3 py-3 align-top">{message.from || "N/A"}</td>
                          <td className="max-w-[300px] break-words px-3 py-3 align-top">{message.subject}</td>
                          <td className="min-w-[240px] px-3 py-3 align-top">{message.otfSummary ?? "N/A"}</td>
                          <td className="min-w-[260px] max-w-[420px] px-3 py-3 align-top">
                            {bodyWarning && (
                              <p
                                className="mb-2 bg-[#FFF8E1] px-2 py-1 text-[#946200]"
                                data-test="admin-monitoring-tech-form-body-warning"
                              >
                                {bodyWarning}
                              </p>
                            )}
                            {message.bodyPreview && (
                              <p className="mb-2 max-h-16 overflow-hidden whitespace-pre-wrap break-words opacity-70">
                                {message.bodyPreview}
                              </p>
                            )}
                            <button
                              type="button"
                              disabled={loadingBodyId !== null}
                              className="font-semibold text-green underline disabled:opacity-40"
                              data-test="admin-monitoring-tech-form-inspect-body"
                              onClick={() => void inspectBody(message.id)}
                            >
                              {loadingBodyId === message.id ? "Reading body" : "Inspect body"}
                            </button>
                            {bodyError && <p className="mt-2 text-[#BF0002]">{bodyError}</p>}
                            {bodyResult && (
                              <div className="mt-2" data-test="admin-monitoring-tech-form-body-result">
                                <p className="mb-1 opacity-60">{bodyResult.contentType} body</p>
                                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words bg-gray-50 p-2 text-xs dark:bg-gray-800">
                                  {bodyResult.body || "No body returned."}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-3 py-5 text-center opacity-70">
                        No emails were returned from the mailbox scan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div
              className="mt-3 flex items-center justify-between gap-3"
              data-test="admin-monitoring-tech-form-pagination"
            >
              <button
                type="button"
                disabled={loading || pageIndex === 0}
                className="border-greyscale-20 h-9 border px-4 text-xs font-semibold disabled:opacity-40 dark:border-gray-700"
                data-test="admin-monitoring-tech-form-previous-page"
                onClick={() => void scanInbox(pageCursors[pageIndex - 1], pageIndex - 1)}
              >
                Previous
              </button>
              <span className="text-xs font-semibold">
                Page {pageIndex + 1} · up to {data.pagination.pageSize} emails
              </span>
              <button
                type="button"
                disabled={loading || !data.pagination.hasNext || !data.pagination.nextCursor}
                className="border-greyscale-20 h-9 border px-4 text-xs font-semibold disabled:opacity-40 dark:border-gray-700"
                data-test="admin-monitoring-tech-form-next-page"
                onClick={() => {
                  const nextCursor = data.pagination.nextCursor;
                  if (!nextCursor) return;
                  setPageCursors((current) => [...current.slice(0, pageIndex + 1), nextCursor]);
                  void scanInbox(nextCursor, pageIndex + 1);
                }}
              >
                Next
              </button>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold">Detected candidate attachments</h3>
            {data.results.length > 0 ? (
              <div className="space-y-3">
                {data.results.map((result, resultIndex) => {
                  const status = result.otfStatus ?? "unknown";
                  const payload = result.payloadSummary;
                  const submission = result.submissionSummary;
                  const diagnosticSummary = getTechFormInboxAttachmentDiagnosticSummary(result);
                  const coreTqRowsStatus = result.sectionSaveHealth?.complete
                    ? "Verified"
                    : result.sectionSaveHealth?.checked
                      ? "Incomplete"
                      : "Not checked";

                  return (
                    <article
                      key={`${result.messageId}-${result.attachmentName}-${resultIndex}`}
                      className="border-greyscale-20 border p-4 dark:border-gray-700"
                      data-test="admin-monitoring-tech-form-json-result"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="break-all text-sm font-semibold">{result.attachmentName}</p>
                          <p className="mt-1 text-xs opacity-70">
                            {formatInboxDate(result.receivedDateTime)} | {result.from || "Unknown sender"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={twMerge("px-2 py-1 text-[10px] font-semibold", statusClasses[status])}>
                            {formatOtfStatus(status)}
                          </span>
                          {result.reingestionRequired && (
                            <button
                              type="button"
                              disabled={reingesting || !data.health.reingestionAllowed}
                              className="bg-[#BF0002] px-3 py-1.5 text-[10px] font-semibold text-white disabled:opacity-50"
                              data-test="admin-monitoring-tech-form-reingest-email"
                              onClick={() =>
                                void reingestTargets([
                                  { messageId: result.messageId, attachmentId: result.attachmentId },
                                ])
                              }
                            >
                              Re-ingest
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 text-xs md:grid-cols-2 xl:grid-cols-3">
                        <div>
                          <p className="font-semibold uppercase opacity-60">Payload</p>
                          <p className="mt-1">Vessel: {payload?.vesselName ?? "N/A"}</p>
                          <p>IMO: {payload?.imoNumber ?? result.imoNumber ?? "N/A"}</p>
                          <p>Offline ID: {payload?.offlineId ?? "N/A"}</p>
                        </div>
                        <div>
                          <p className="font-semibold uppercase opacity-60">Data summary</p>
                          <p className="mt-1">Sections: {payload?.populatedSections.join(", ") || "None"}</p>
                          <p>
                            Contacts {payload?.contactCount ?? 0}, draft rows {payload?.draftRowCount ?? 0}, ports{" "}
                            {payload?.portCallCount ?? 0}, power rows {payload?.powerManagementRowCount ?? 0}
                          </p>
                        </div>
                        <div>
                          <p className="font-semibold uppercase opacity-60">Snowflake</p>
                          <p className="mt-1">Status: {submission?.submitStatus ?? "N/A"}</p>
                          <p>Save strategy: {submission?.saveStrategy ?? "N/A"}</p>
                          <p>Stored JSON: {submission?.storedJsonStatus.replaceAll("_", " ") ?? "N/A"}</p>
                          <p>Core TQ rows: {coreTqRowsStatus}</p>
                          {result.sectionSaveHealth && result.sectionSaveHealth.missingSections.length > 0 && (
                            <p className="text-[#BF0002]">
                              Missing: {result.sectionSaveHealth.missingSections.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="mt-3 text-xs">{result.otfStatusReason ?? result.reason ?? "No status detail."}</p>
                      {diagnosticSummary && (
                        <p
                          className="mt-2 break-words text-xs opacity-70"
                          data-test="admin-monitoring-tech-form-attachment-diagnostics"
                        >
                          {diagnosticSummary}
                        </p>
                      )}
                      {result.status === "unreadable" && result.reason && result.reason !== result.otfStatusReason && (
                        <p
                          className="mt-2 break-words text-xs"
                          data-test="admin-monitoring-tech-form-attachment-failure-reason"
                        >
                          {result.reason}
                        </p>
                      )}
                      {result.warnings && result.warnings.length > 0 && (
                        <p className="mt-2 bg-[#FFF8E1] px-3 py-2 text-xs text-[#946200]">
                          {result.warnings.join(" ")}
                        </p>
                      )}

                      {result.json !== undefined && (
                        <details className="mt-3 bg-gray-50 p-3 dark:bg-gray-800">
                          <summary className="cursor-pointer text-xs font-semibold">Inspect received JSON</summary>
                          <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words text-xs leading-5">
                            {JSON.stringify(result.json, null, 2)}
                          </pre>
                        </details>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="border-greyscale-20 border border-dashed px-4 py-5 text-sm opacity-70 dark:border-gray-700">
                No candidate attachments were found in the scanned messages.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default TechFormInboxDebugPanel;
